import { AUTH_STORAGE_KEYS, AUTH_CONFIG } from '../constants/auth';

export interface StorageAdapter {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  clearAuthData(): Promise<void>;
}

class UnifiedStorageAdapter implements StorageAdapter {
  private async withRetry<T>(
    operation: () => Promise<T>,
    attempts = AUTH_CONFIG.STORAGE_RETRY_ATTEMPTS
  ): Promise<T> {
    for (let i = 0; i < attempts; i++) {
      try {
        return await Promise.race([
          operation(),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Storage timeout')), 
              AUTH_CONFIG.STORAGE_OPERATION_TIMEOUT)
          )
        ]);
      } catch (error) {
        if (i === attempts - 1) throw error;
        await new Promise(resolve => 
          setTimeout(resolve, AUTH_CONFIG.RETRY_DELAY_BASE * Math.pow(AUTH_CONFIG.RETRY_BACKOFF_FACTOR, i))
        );
      }
    }
    throw new Error('Storage operation failed after retries');
  }

  async getItem(key: string): Promise<string | null> {
    return this.withRetry(async () => {
      try {
        return localStorage.getItem(key);
      } catch {
        return sessionStorage.getItem(key);
      }
    });
  }

  async setItem(key: string, value: string): Promise<void> {
    return this.withRetry(async () => {
      try {
        localStorage.setItem(key, value);
      } catch {
        sessionStorage.setItem(key, value);
      }
    });
  }

  async removeItem(key: string): Promise<void> {
    return this.withRetry(async () => {
      try {
        localStorage.removeItem(key);
      } catch {}
      try {
        sessionStorage.removeItem(key);
      } catch {}
    });
  }

  async clearAuthData(): Promise<void> {
    return this.withRetry(async () => {
      // Only clear our specific auth keys, not everything
      const authKeys = Object.values(AUTH_STORAGE_KEYS);
      
      for (const key of authKeys) {
        try {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        } catch {}
      }
    });
  }
}

export const unifiedStorage = new UnifiedStorageAdapter();
